import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormBuilderModal from './FormBuilderModal';
import OnboardingQuillEditor from './OnboardingQuillEditor';
import './OnboardingWorkflow.css';

/**
 * Automation types available for automated tasks
 */
const AUTOMATION_TYPES = [
    {
        id: 'scheduled_email',
        title: 'Email Automation',
        description: 'Send a scheduled or immediate onboarding email',
        icon: 'fa-envelope-open-text',
        hasEmailDraft: true
    },
    {
        id: 'document_request',
        title: 'Request Documents',
        description: 'Send email requesting documents + require upload in employee portal',
        icon: 'fa-file-import',
        hasEmailDraft: true,
        hasPortalIntegration: true
    },
    {
        id: 'document_send',
        title: 'Send Documents',
        description: 'Send documents attached via email (upload or link)',
        icon: 'fa-file-export',
        hasEmailDraft: true,
        hasAttachments: true
    },
    {
        id: 'form_collect',
        title: 'Send Form',
        description: 'Collect information via form, exportable as CSV',
        icon: 'fa-clipboard-list',
        hasFormBuilder: true,
        hasExport: true,
        hasEmailDraft: true // Enable email for sending the form link
    },
    {
        id: 'meeting_schedule',
        title: 'Schedule Meeting',
        description: 'Schedule meetings with calendar integration',
        icon: 'fa-calendar-plus',
        hasCalendarIntegration: true
    }
];

/**
 * Modal for creating automated tasks with multiple automation types
 * Supports chaining multiple automated tasks together
 */
const AddAutomatedTaskModal = ({
    onClose,
    onSave,
    hire,
    stage,
    editingTask = null,
    templateMode = false
}) => {
  useUiLanguage();
    const { t } = useTranslation('onboarding');
    const isEditing = Boolean(editingTask);
    const inferAutomationType = (task = {}) => {
        if (task?.automationType) return task.automationType;
        if (task?.formConfig || task?.formLink) return 'form_collect';
        if (task?.meetingConfig) return 'meeting_schedule';
        if (task?.documentUploadLink || task?.portalUploadRequired || (Array.isArray(task?.requiredDocuments) && task.requiredDocuments.length > 0)) {
            return 'document_request';
        }
        if (Array.isArray(task?.attachments) && task.attachments.length > 0) return 'document_send';
        return task?.type === 'automated' ? 'scheduled_email' : null;
    };
    const parseRequiredDocuments = (value) => (
        (value || '')
            .split(/\n|,|;/)
            .map(item => item.trim())
            .filter(Boolean)
    );

    const buildInitialTaskState = (task = null) => {
        if (!task) {
            return {
                title: '',
                automationType: null,
                dueDate: '',
                relativeDays: 0,
                executionMode: 'scheduled', // 'scheduled' or 'immediate'
                emailDraft: { subject: '', body: '' },
                attachments: [],
                formConfig: null,
                meetingConfig: null,
                requiredDocumentsText: ''
            };
        }

        const requiredDocumentsText = Array.isArray(task.requiredDocuments)
            ? task.requiredDocuments.join('\n')
            : '';
        return {
            title: task.title || '',
            automationType: inferAutomationType(task),
            dueDate: task.dueDate || '',
            relativeDays: Number.isFinite(Number(task.relativeDays)) ? Number(task.relativeDays) : 0,
            executionMode: task.executionMode || 'scheduled',
            emailDraft: {
                subject: task.emailDraft?.subject || '',
                body: task.emailDraft?.body || ''
            },
            attachments: Array.isArray(task.attachments) ? task.attachments : [],
            formConfig: task.formConfig || null,
            meetingConfig: task.meetingConfig || null,
            requiredDocumentsText
        };
    };

    const [currentTask, setCurrentTask] = useState({
        ...buildInitialTaskState(editingTask)
    });

    const [chainedTasks, setChainedTasks] = useState([]);
    const [showFormBuilder, setShowFormBuilder] = useState(false);

    const selectedType = AUTOMATION_TYPES.find(t => t.id === currentTask.automationType);
    const isBeforeStartStage = stage === 'Before Day One';
    const startDayOffsetLabel = isBeforeStartStage
        ? t('templatesEditor.drawer.daysBeforeStartDay', 'Days Before Start Day')
        : t('templatesEditor.drawer.daysAfterStartDay', 'Days After Start Day');
    const displayedRelativeDays = currentTask.executionMode === 'immediate'
        ? 0
        : Math.abs(Number.parseInt(currentTask.relativeDays, 10) || 0);

    const withRequiredDocuments = (task) => {
        const requiredDocuments = parseRequiredDocuments(task?.requiredDocumentsText);
        const normalized = {
            ...task
        };
        delete normalized.requiredDocumentsText;
        if (requiredDocuments.length > 0) {
            normalized.requiredDocuments = requiredDocuments;
            normalized.documentCollectionMode = 'candidate_upload';
        }
        return normalized;
    };

    const handleAutomationTypeSelect = (typeId) => {
        const type = AUTOMATION_TYPES.find(t => t.id === typeId);
        // Set default email body for form collection
        let defaultEmailBody = '';
        if (typeId === 'form_collect') {
            defaultEmailBody = `Hi ${hire?.name || 'there'},\n\nPlease fill out the following form to provide the necessary information for your onboarding:\n\n{{form_link}}\n\nThank you!`;
        }

        setCurrentTask({
            ...currentTask,
            automationType: typeId,
            title: currentTask.title || type.title,
            emailDraft: {
                ...currentTask.emailDraft,
                subject: typeId === 'form_collect' ? 'Required Information Form' : currentTask.emailDraft.subject,
                body: defaultEmailBody || currentTask.emailDraft.body
            },
            requiredDocumentsText: typeId === 'document_request' ? currentTask.requiredDocumentsText : ''
        });
    };

    const handleAddToChain = () => {
        if (!currentTask.automationType || !currentTask.title) {
            alert(uiText('Please select an automation type and enter a title'));
            return;
        }

        const normalizedTask = withRequiredDocuments(currentTask);
        const preparedTask = templateMode
            ? {
                ...normalizedTask,
                relativeDays: normalizedTask.executionMode === 'immediate'
                    ? 0
                    : (Number.parseInt(normalizedTask.relativeDays, 10) || 0),
                dueDate: ''
            }
            : normalizedTask;

        setChainedTasks([...chainedTasks, preparedTask]);

        // Reset for next task
        setCurrentTask({
            title: '',
            automationType: null,
            dueDate: currentTask.dueDate, // Keep the same date
            relativeDays: currentTask.relativeDays,
            executionMode: 'scheduled',
            emailDraft: { subject: '', body: '' },
            attachments: [],
            formConfig: null,
            meetingConfig: null,
            requiredDocumentsText: ''
        });
    };

    const handleRemoveFromChain = (index) => {
        setChainedTasks(chainedTasks.filter((_, i) => i !== index));
    };

    const handleSave = () => {
        // Add current task to chain if it's configured
        let tasksToSave = [...chainedTasks];
        if (currentTask.automationType && currentTask.title) {
            const normalizedTask = withRequiredDocuments(currentTask);
            tasksToSave.push(
                templateMode
                    ? {
                        ...normalizedTask,
                        relativeDays: normalizedTask.executionMode === 'immediate'
                            ? 0
                            : (Number.parseInt(normalizedTask.relativeDays, 10) || 0),
                        dueDate: ''
                    }
                    : normalizedTask
            );
        }

        if (tasksToSave.length === 0) {
            alert(uiText('Please configure at least one automation task'));
            return;
        }

        onSave(tasksToSave, stage);
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        const newAttachments = files.map(file => ({
            name: file.name,
            file: file,
            type: file.type
        }));
        setCurrentTask({
            ...currentTask,
            attachments: [...currentTask.attachments, ...newAttachments]
        });
    };
    const handleRemoveAttachment = (index) => {
        setCurrentTask(prev => ({
            ...prev,
            attachments: (prev.attachments || []).filter((_, i) => i !== index)
        }));
    };

    return (
        <>
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content-onboarding add-task-modal" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>{isEditing ? t('automatedTaskModal.editTitle', 'Edit Automated Task') : t('automatedTaskModal.setupTitle', 'Set Up Automated Task')}</h2>
                        <button className="close" onClick={onClose}>&times;</button>
                    </div>

                    <div className="modal-body-onboarding">
                        {/* Automation Type Selection */}
                        <div className="form-group">
                            <label>{t('automatedTaskModal.selectType', 'Select Automation Type')}</label>
                            <div className="automation-type-grid">
                                {AUTOMATION_TYPES.map(type => (
                                    <button
                                        key={type.id}
                                        type="button"
                                        className={`automation-type-btn ${currentTask.automationType === type.id ? 'active' : ''}`}
                                        onClick={() => handleAutomationTypeSelect(type.id)}
                                    >
                                        <i className={`fa-solid ${type.icon}`} />
                                        <div className="type-title">{t(`automatedTaskModal.types.${type.id}.title`, type.title)}</div>
                                        <div className="type-desc">{t(`automatedTaskModal.types.${type.id}.description`, type.description)}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Task Title */}
                        <div className="form-group">
                            <label>{t('automatedTaskModal.taskTitle', 'Task Title')}</label>
                            <input
                                type="text"
                                value={currentTask.title}
                                onChange={e => setCurrentTask({ ...currentTask, title: e.target.value })}
                                placeholder={t('automatedTaskModal.taskTitlePlaceholder', 'e.g., Send NDA Documents')}
                            />
                        </div>

                        {/* Due Date/Offset & Execution Mode */}
                        <div className="form-row">
                            <div className="form-group">
                                <label>{templateMode ? t('automatedTaskModal.timing', 'Timing') : t('automatedTaskModal.execution', 'Execution')}</label>
                                <select
                                    value={currentTask.executionMode}
                                    onChange={e => setCurrentTask({
                                        ...currentTask,
                                        executionMode: e.target.value,
                                        relativeDays: e.target.value === 'immediate' ? 0 : currentTask.relativeDays
                                    })}
                                >
                                    <option value="scheduled">{t('automatedTaskModal.scheduledOption', 'On Scheduled Date')}</option>
                                    <option value="immediate">{t('automatedTaskModal.immediateOption', 'Immediately')}</option>
                                </select>
                            </div>
                            {templateMode ? (
                                <div className="form-group">
                                    <label>{startDayOffsetLabel}</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={displayedRelativeDays}
                                        onChange={e => {
                                            const days = Math.max(0, Number.parseInt(e.target.value, 10) || 0);
                                            setCurrentTask({ ...currentTask, relativeDays: isBeforeStartStage ? -days : days });
                                        }}
                                        disabled={currentTask.executionMode === 'immediate'}
                                    />
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label>{t('automatedTaskModal.scheduledDate', 'Scheduled Date')}</label>
                                    <input
                                        type="date"
                                        value={currentTask.dueDate}
                                        onChange={e => setCurrentTask({ ...currentTask, dueDate: e.target.value })}
                                    />
                                </div>
                            )}
                        </div>
                        {templateMode && currentTask.executionMode === 'immediate' && (
                            <small style={{ color: '#94a3b8' }}>
                                {t('automatedTaskModal.immediateHint', 'Immediate tasks run as soon as the new hire is added to onboarding.')}
                            </small>
                        )}

                        {/* Email Draft Section (for email automations) */}
                        {selectedType?.hasEmailDraft && (
                            <div className="email-draft-section">
                                <div className="section-header-small">
                                    <label>{t('automatedTaskModal.emailDraft', 'Email Draft')}</label>
                                </div>

                                <div className="form-group">
                                    <label>{t('modals.addTask.emailSubject', 'Subject')}</label>
                                    <input
                                        type="text"
                                        value={currentTask.emailDraft.subject}
                                        onChange={e => setCurrentTask({
                                            ...currentTask,
                                            emailDraft: { ...currentTask.emailDraft, subject: e.target.value }
                                        })}
                                        placeholder={t('modals.addTask.emailSubjectPlaceholder', 'Email subject...')}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>{t('modals.addTask.emailBody', 'Message')}</label>
                                    <OnboardingQuillEditor
                                        className="onboarding-quill"
                                        value={currentTask.emailDraft.body}
                                        onChange={(value) => setCurrentTask({
                                            ...currentTask,
                                            emailDraft: { ...currentTask.emailDraft, body: value }
                                        })}
                                    />
                                </div>
                            </div>
                        )}

                        {selectedType?.hasAttachments && (
                            <div className="form-group">
                                <label>{t('automatedTaskModal.attachDocuments', 'Attach Documents')}</label>
                                <input
                                    type="file"
                                    multiple
                                    onChange={handleFileUpload}
                                />
                                {currentTask.attachments?.length > 0 && (
                                    <div className="attachments-list" style={{ marginTop: '10px' }}>
                                        {currentTask.attachments.map((att, idx) => (
                                            <div key={`${att.name}_${idx}`} className="attachment-item">
                                                <i className="fa-solid fa-paperclip" />
                                                <span>{att.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveAttachment(idx)}
                                                    aria-label={t('automatedTaskModal.removeAttachment', 'Remove attachment')}
                                                >
                                                    <i className="fa-solid fa-xmark" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Form Builder trigger (for form_collect) */}
                        {selectedType?.hasFormBuilder && (
                            <div className="form-group">
                                <label>{t('automatedTaskModal.formConfiguration', 'Form Configuration')}</label>
                                <button
                                    type="button"
                                    className="btn-secondary full-width"
                                    onClick={() => setShowFormBuilder(true)}
                                >
                                    <i className="fa-solid fa-plus" />
                                    {currentTask.formConfig ? t('automatedTaskModal.editFormFields', 'Edit Form Fields') : t('automatedTaskModal.configureFormFields', 'Configure Form Fields')}
                                </button>
                                {currentTask.formConfig && (
                                    <div className="form-config-preview">
                                        <span>{t('automatedTaskModal.fieldsConfigured', { defaultValue: '{{count}} fields configured', count: currentTask.formConfig.fields?.length || 0 })}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {currentTask.automationType === 'document_request' && (
                            <div className="form-group">
                                <label>{t('automatedTaskModal.requiredDocuments', 'Required Documents')}</label>
                                <textarea
                                    value={currentTask.requiredDocumentsText}
                                    onChange={e => setCurrentTask({ ...currentTask, requiredDocumentsText: e.target.value })}
                                    placeholder={t('automatedTaskModal.requiredDocumentsPlaceholder', 'One document per line, e.g.\nGovernment ID\nTax Form')}
                                    rows={4}
                                />
                                <small style={{ color: '#94a3b8' }}>
                                    {t('automatedTaskModal.requiredDocumentsHint', 'These document names will appear as upload buckets for the new hire.')}
                                </small>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn-secondary" onClick={onClose}>
                            {t('buttons.cancel', 'Cancel')}
                        </button>
                        <button type="button" className="btn-primary" onClick={handleSave}>
                            {isEditing ? t('humanTaskModal.saveTask', 'Save Task') : t('humanTaskModal.createTask', 'Create Task')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Form Builder Modal */}
            {showFormBuilder && (
                <FormBuilderModal
                    onClose={() => setShowFormBuilder(false)}
                    onSave={(formConfig) => {
                        setCurrentTask({
                            ...currentTask,
                            formConfig
                        });
                        setShowFormBuilder(false);
                    }}
                    initialConfig={currentTask.formConfig}
                />
            )}
        </>
    );
};

export default AddAutomatedTaskModal;
export { AUTOMATION_TYPES };
